import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { usePageTitle } from '../../components/common/PageTitleContext'
import type { MeResponse } from '../../api/me'
import useContactsListStore, {
  type BlockFilter,
  type IdentityPresenceFilter,
} from '../../store/contactsList'
import type { ContactListItem } from '../../api/contacts'
import useStationStore from '../../store/station'
import {
  formatMessageTimestamp,
  formatStationTime,
  resolveEpochMs,
  resolveStationTimeZone,
} from '../../utils/time'

const PLATFORM_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'telegram', label: 'Telegram' },
]

const BLOCKED_OPTIONS: { value: BlockFilter; label: string }[] = [
  { value: 'any', label: 'Any status' },
  { value: 'blocked', label: 'Only blocked' },
  { value: 'unblocked', label: 'Only unblocked' },
]

const IDENTITY_OPTIONS: { value: IdentityPresenceFilter; label: string }[] = [
  { value: 'any', label: 'All contacts' },
  { value: 'with', label: 'With identities' },
  { value: 'without', label: 'Without identities' },
]

type OutletContext = { me: MeResponse }

const ContactRow: React.FC<{ contact: ContactListItem; onOpen: (id: number) => void }>
 = ({ contact, onOpen }) => {
  const handleClick = () => onOpen(contact.id)
  const stationTimezone = useStationStore((state) => state.activeStation?.timezone)
  const lastActivityEpochMs = React.useMemo(
    () =>
      resolveEpochMs({
        epochSeconds: contact.last_activity_at_epoch ?? undefined,
        isoString: contact.last_activity_at_iso_utc ?? contact.last_activity_at ?? null,
      }),
    [contact.last_activity_at, contact.last_activity_at_epoch, contact.last_activity_at_iso_utc],
  )
  const lastActivityLabel = lastActivityEpochMs
    ? formatMessageTimestamp(lastActivityEpochMs, stationTimezone)
    : 'No recent activity'
  const absoluteLabel = React.useMemo(() => {
    if (!lastActivityEpochMs) {
      return contact.last_activity_at ?? 'No recent activity'
    }
    const tz = resolveStationTimeZone(stationTimezone)
    return formatStationTime(lastActivityEpochMs, tz, { dateStyle: 'medium', timeStyle: 'short' })
  }, [contact.last_activity_at, lastActivityEpochMs, stationTimezone])

  return (
    <Paper
      component="li"
      variant="outlined"
      sx={{
        listStyle: 'none',
        p: 2,
        borderRadius: 3,
        cursor: 'pointer',
        transition: (theme) => theme.transitions.create(['box-shadow', 'border-color']),
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.light',
        },
      }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(contact.id)
        }
      }}
      aria-label={`Open contact ${contact.name || contact.id}`}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack spacing={0.5}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {contact.name || 'Untitled contact'}
            </Typography>
            {contact.location && (
              <Typography variant="body2" color="text.secondary">
                {contact.location}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {contact.blocked && <Chip label="Blocked" color="error" size="small" />}
            <Typography variant="body2" color="text.secondary" title={absoluteLabel}>
              {lastActivityLabel}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {contact.identities_summary.map((summary) => (
            <Chip
              key={`${contact.id}-${summary.platform}`}
              label={`${summary.platform}×${summary.count}`}
              color={summary.blocked_count > 0 ? 'warning' : 'default'}
              size="small"
            />
          ))}
          {contact.identities_summary.length === 0 && (
            <Chip label="No linked identities" size="small" variant="outlined" />
          )}
        </Stack>
        {contact.notes_preview && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {contact.notes_preview}
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}

const ContactsPage: React.FC = () => {
  const { me } = useOutletContext<OutletContext>()
  const stationId = me.stations[0]?.id ?? 0
  const navigate = useNavigate()
  const searchInputRef = React.useRef<HTMLInputElement | null>(null)

  const filters = useContactsListStore((state) => state.filters)
  const items = useContactsListStore((state) => state.items)
  const cursor = useContactsListStore((state) => state.cursor)
  const loading = useContactsListStore((state) => state.loading)
  const error = useContactsListStore((state) => state.error)
  const initialized = useContactsListStore((state) => state.initialized)
  const applyFilters = useContactsListStore((state) => state.applyFilters)
  const refresh = useContactsListStore((state) => state.refresh)
  const loadMore = useContactsListStore((state) => state.loadMore)

  const [search, setSearch] = React.useState(filters.q)

  usePageTitle('Contacts', { align: 'center' })

  React.useEffect(() => {
    setSearch(filters.q)
  }, [filters.q])

  React.useEffect(() => {
    if (!stationId) {
      return
    }
    void refresh(stationId)
  }, [stationId, refresh])

  React.useEffect(() => {
    if (!stationId) {
      return
    }
    const handler = (event: KeyboardEvent) => {
      if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey) {
        const target = event.target as HTMLElement | null
        const tag = target?.tagName
        if (tag && ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) {
          return
        }
        event.preventDefault()
        searchInputRef.current?.focus()
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [stationId])

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      void applyFilters(stationId, (prev) => ({ ...prev, q: search.trimStart() }))
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [search, applyFilters, stationId])

  const handlePlatformChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value
    const next = Array.isArray(value) ? (value as string[]) : []
    void applyFilters(stationId, (prev) => ({ ...prev, platform: next }))
  }

  const handleBlockedChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as BlockFilter
    void applyFilters(stationId, (prev) => ({ ...prev, blocked: value }))
  }

  const handleIdentityChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as IdentityPresenceFilter
    void applyFilters(stationId, (prev) => ({ ...prev, hasIdentities: value }))
  }

  const handleSortChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as typeof filters.sort
    void applyFilters(stationId, (prev) => ({ ...prev, sort: value }))
  }

  const handleClearFilters = () => {
    void applyFilters(stationId, () => ({
      q: '',
      platform: [],
      blocked: 'any',
      hasIdentities: 'any',
      sort: 'last_activity_desc',
    }))
  }

  const openContact = (id: number) => {
    navigate(`/inbox/contacts/${id}`)
  }

  const renderList = () => {
    if (!loading && initialized && items.length === 0) {
      return (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">No contacts match these filters.</Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or clearing filters to see more contacts.
            </Typography>
            <Button variant="outlined" onClick={handleClearFilters} sx={{ alignSelf: 'flex-start' }}>
              Clear filters
            </Button>
          </Stack>
        </Alert>
      )
    }

    return (
      <Stack component="ul" spacing={2} sx={{ p: 0, m: 0 }}>
        {items.map((contact) => (
          <ContactRow key={contact.id} contact={contact} onOpen={openContact} />
        ))}
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '320px 1fr' },
        gap: 4,
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
      }}
      role="main"
      aria-label="Contacts manager"
    >
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          position: { lg: 'sticky' },
          top: { lg: 96 },
          height: { lg: 'fit-content' },
        }}
        component="section"
        aria-labelledby="contacts-filter-heading"
      >
        <Stack spacing={3}>
          <Typography id="contacts-filter-heading" variant="h6">
            Filters
          </Typography>
          <TextField
            inputRef={searchInputRef}
            label="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, email, notes…"
            InputProps={{ endAdornment: <Typography variant="caption">/</Typography> }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="contacts-platform-label">Platform</InputLabel>
            <Select
              labelId="contacts-platform-label"
              multiple
              value={filters.platform}
              onChange={handlePlatformChange}
              input={<OutlinedInput label="Platform" />}
              renderValue={(selected) =>
                (selected as string[])
                  .map((value) => PLATFORM_OPTIONS.find((option) => option.value === value)?.label ?? value)
                  .join(', ')
              }
            >
              {PLATFORM_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="contacts-blocked-label">Blocked</InputLabel>
            <Select
              labelId="contacts-blocked-label"
              value={filters.blocked}
              label="Blocked"
              onChange={handleBlockedChange}
            >
              {BLOCKED_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="contacts-identities-label">Identities</InputLabel>
            <Select
              labelId="contacts-identities-label"
              value={filters.hasIdentities}
              label="Identities"
              onChange={handleIdentityChange}
            >
              {IDENTITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <FormLabel id="contacts-sort-label" sx={{ fontSize: 12, color: 'text.secondary' }}>
              Sort by
            </FormLabel>
            <Select
              aria-labelledby="contacts-sort-label"
              value={filters.sort}
              onChange={handleSortChange}
            >
              <MenuItem value="last_activity_desc">Recent activity</MenuItem>
              <MenuItem value="name_asc">Name A–Z</MenuItem>
              <MenuItem value="created_desc">Newest contacts</MenuItem>
            </Select>
          </FormControl>
          <Divider sx={{ my: 1 }} />
          <Button variant="outlined" onClick={handleClearFilters} disabled={
            filters.q === '' &&
            filters.platform.length === 0 &&
            filters.blocked === 'any' &&
            filters.hasIdentities === 'any' &&
            filters.sort === 'last_activity_desc'
          }>
            Clear filters
          </Button>
        </Stack>
      </Paper>
      <Box component="section" aria-live="polite" aria-busy={loading}>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" onClose={() => void refresh(stationId)}>
              {error}
            </Alert>
          )}
          {renderList()}
          {cursor && (
            <Button
              onClick={() => void loadMore(stationId)}
              variant="outlined"
              disabled={loading}
              sx={{ alignSelf: 'center' }}
            >
              Load more contacts
            </Button>
          )}
          {loading && items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Loading contacts…
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  )
}

export default ContactsPage
